import React from 'react';
import { getInitials } from '../../utils/nameUtils';
import { getGenderAvatarBg } from '../../utils/genderColors';

export default function CourtPanel({
  title,
  headerRight,
  teams = [],
  playersById = {},
  renderTeamPanel,
  actions,
  /**
   * Score column split into three rows aligned with team1 / vs / team2, plus footer (e.g. submit).
   * When set, uses a shared grid so the mid score divider lines up with the team vs row.
   */
  scoreRail = null,
  /** Full-width block below the main court body, e.g. pending state or errors. */
  bodyFooter = null,
  minHeight = null,
  className = '',
  isUpcoming = false,
  /** 'comfortable' | 'compact' — tighter padding for dense court grids */
  density = 'comfortable',
  /** Brief competitive UX: court that just received the next lineup after a score */
  highlightFreshRotation = false,
  /** Peg Board: viewer's turn to pick — primary border so the court is the CTA */
  highlightCallToAction = false,
  /** 'stack' (default) | 'sideBySide' — sideBySide renders a compact two-column layout on mobile (<md) */
  teamsLayout = 'stack',
  /** Viewer's player ID — used to highlight "(You)" in the side-by-side mobile layout */
  viewerPlayerId = null,
  /** Optional strip between the court header and team slots (e.g. Peg Board picker hint). */
  banner = null,
  /** Replaces team/score body — e.g. member Peg Board empty court. */
  emptyBody = null,
  /** Skip team/score body (use with emptyBody, or with a footer CTA only). */
  hideTeams = false,
}) {
  const hasActionsFooter = Boolean(actions);
  const hasBodyBelowHeader = Boolean(scoreRail) || Boolean(bodyFooter);
  const resolvedMinHeight =
    minHeight === 'auto'
      ? 'auto'
      : minHeight != null
        ? minHeight
        : isUpcoming
          ? 'auto'
          : 240;
  const compact = density === 'compact';
  const surfaceBorder = highlightFreshRotation
    ? '!border-2 !border-emerald-500 dark:!border-emerald-400 ring-2 ring-emerald-500/45 dark:ring-emerald-400/35'
    : highlightCallToAction
      ? '!border-2 !border-primary ring-2 ring-primary/40'
      : 'border border-black/8 dark:border-white/8';
  const shadowMotion = highlightFreshRotation
    ? 'shadow-brand-md shadow-emerald-500/20'
    : highlightCallToAction
      ? 'shadow-brand-md shadow-primary/25'
      : 'shadow-brand-sm hover:shadow-brand-md hover:-translate-y-0.5';

  const vsDividerRow = (
    <div
      className={`flex w-full items-center gap-2 ${compact ? 'py-0' : 'py-0.5'}`}
      aria-hidden
    >
      <div className="h-px min-w-0 flex-1 bg-black/8 dark:bg-white/10" />
      <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-text-secondary sm:text-xs">
        vs
      </span>
      <div className="h-px min-w-0 flex-1 bg-black/8 dark:bg-white/10" />
    </div>
  );

  const scoreMidRule = (
    <div className="flex h-full min-h-[1rem] w-full items-center px-0" aria-hidden>
      <div className="h-px w-full bg-black/10 dark:bg-white/15" />
    </div>
  );

  const mainBodyPadding = `${compact ? 'gap-1 px-2.5 pt-1' : 'gap-2 px-3.5 pt-2'} ${
    hasBodyBelowHeader || hasActionsFooter ? 'pb-0.5' : compact ? 'pb-1' : 'pb-3.5'
  }`;

  const gridBodyPadding = `${compact ? 'px-2.5 pt-1' : 'px-3.5 pt-2'} ${
    hasBodyBelowHeader || hasActionsFooter ? 'pb-0.5' : compact ? 'pb-1' : 'pb-3.5'
  }`;

  const railXPad = compact ? 'pl-1.5' : 'pl-2';
  /** Narrow score rail (2-digit scores) frees horizontal space for team panels on playing courts. */
  const gridTemplateColumns = compact
    ? 'minmax(0, 1fr) 1px minmax(2.5rem, 3.25rem)'
    : 'minmax(0, 1fr) 1px minmax(3rem, 3.75rem)';

  const titleCluster = (
    <>
      {isUpcoming && (
        <span
          className={`shrink-0 ${compact ? 'text-sm lg:text-base' : 'text-base lg:text-lg'}`}
          title="Next up"
        >
          ⏳
        </span>
      )}
      <div
        className={`min-w-0 font-semibold text-text-primary [&>*]:min-w-0 ${compact ? 'text-sm lg:text-base' : 'text-base lg:text-lg'}`}
      >
        {title}
      </div>
      {isUpcoming && (
        <span className="shrink-0 px-1.5 py-0.5 text-[10px] font-semibold bg-info-light text-info-dark rounded-full uppercase tracking-wide">
          Next
        </span>
      )}
    </>
  );

  const teamsStack = (
    <>
      <div className="min-w-0 w-full">
        {renderTeamPanel(teams[0], 1, playersById)}
      </div>
      {vsDividerRow}
      <div className="min-w-0 w-full">
        {renderTeamPanel(teams[1], 2, playersById)}
      </div>
    </>
  );

  const renderMobileTeamColumn = (teamIds, teamNumber) => {
    const borderColor = teamNumber === 1 ? 'border-team1' : 'border-team2';
    const labelColor = teamNumber === 1 ? 'text-team1' : 'text-team2';
    return (
      <div className={`flex flex-col border-l-2 ${borderColor} pl-2 min-w-0`}>
        <span className={`text-[11px] font-semibold uppercase tracking-wide ${labelColor} mb-0.5`}>
          Team {teamNumber}
        </span>
        {(teamIds || []).map((id) => {
          const player = playersById[id];
          if (!player) return null;
          const isViewer = viewerPlayerId && id === viewerPlayerId;
          return (
            <div key={id} className="flex items-center gap-1.5 py-0.5 min-w-0">
              <span
                className={`h-5 w-5 rounded-full text-[10px] flex items-center justify-center text-white font-bold flex-shrink-0 ${getGenderAvatarBg(player.gender)}`}
              >
                {getInitials(player.name)}
              </span>
              <span className="text-sm font-semibold truncate leading-tight min-w-0" title={player.name || undefined}>
                {player.name}
              </span>
              {isViewer && (
                <span className="text-[10px] font-semibold text-primary flex-shrink-0">(You)</span>
              )}
            </div>
          );
        })}
        {(teamIds || []).length === 0 && (
          <span className="text-xs text-text-muted italic py-0.5">—</span>
        )}
      </div>
    );
  };

  const mobileSideBySideBody = (
    <div className="grid grid-cols-[1fr_auto_1fr] items-start gap-x-3">
      {renderMobileTeamColumn(teams[0], 1)}
      <div className="self-start pt-[18px]">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-text-muted">vs</span>
      </div>
      {renderMobileTeamColumn(teams[1], 2)}
    </div>
  );

  return (
    <div
      className={`h-full rounded-xl flex flex-col bg-surface ${surfaceBorder} ${shadowMotion} transition-all duration-200 overflow-visible ${className}`}
      style={{ minHeight: resolvedMinHeight }}
    >
      <div
        className={`flex min-w-0 items-center justify-between gap-2 ${compact ? 'px-2.5 pt-1.5 pb-1' : 'px-3.5 pt-2.5 pb-2'}`}
      >
        <div className="flex min-w-0 flex-1 items-center gap-1.5 sm:gap-2">
          {titleCluster}
        </div>
        {headerRight && (
          <div className="flex shrink-0 items-center">{headerRight}</div>
        )}
      </div>

      <div
        className={`border-t border-black/5 dark:border-white/5 ${compact ? 'mx-2.5' : 'mx-3.5'}`}
      />

      {banner ? (
        <div className={compact ? 'px-2.5 pt-1.5' : 'px-3.5 pt-2'}>{banner}</div>
      ) : null}

      {emptyBody || hideTeams ? (
        emptyBody ? (
          <div className={`flex min-h-0 w-full flex-1 flex-col justify-center ${compact ? 'px-2.5 py-2' : 'px-3.5 py-3'}`}>
            {emptyBody}
          </div>
        ) : null
      ) : scoreRail ? (
        <div
          className={`grid w-full min-w-0 grid-rows-[auto_auto_auto_auto] items-stretch gap-0 ${gridBodyPadding}`}
          style={{ gridTemplateColumns }}
        >
          <div className="col-start-1 row-start-1 min-w-0 self-stretch">
            {renderTeamPanel(teams[0], 1, playersById)}
          </div>
          <div
            className="col-start-2 row-span-4 row-start-1 w-px justify-self-center bg-black/10 dark:bg-white/15"
            aria-hidden
          />
          <div
            className={`col-start-3 row-start-1 flex min-h-0 min-w-0 flex-col justify-center self-stretch ${railXPad}`}
          >
            {scoreRail.team1}
          </div>

          <div className="col-start-1 row-start-2 min-w-0">{vsDividerRow}</div>
          <div
            className={`col-start-3 row-start-2 flex min-h-0 items-center self-stretch ${railXPad}`}
          >
            {scoreMidRule}
          </div>

          <div className="col-start-1 row-start-3 min-w-0 self-stretch">
            {renderTeamPanel(teams[1], 2, playersById)}
          </div>
          <div
            className={`col-start-3 row-start-3 flex min-h-0 min-w-0 flex-col justify-center self-stretch ${railXPad}`}
          >
            {scoreRail.team2}
          </div>

          <div
            className={`col-start-3 row-start-4 flex min-w-0 flex-col justify-end pb-0.5 pt-1 ${railXPad}`}
          >
            {scoreRail.footer}
          </div>
        </div>
      ) : teamsLayout === 'sideBySide' ? (
        <>
          <div className={`md:hidden flex w-full flex-col ${mainBodyPadding}`}>
            {mobileSideBySideBody}
          </div>
          <div className={`hidden md:flex w-full flex-col ${mainBodyPadding}`}>
            {teamsStack}
          </div>
        </>
      ) : (
        <div className={`flex w-full flex-col ${mainBodyPadding}`}>{teamsStack}</div>
      )}

      {bodyFooter && (
        <div
          className={`border-t border-black/5 dark:border-white/5 ${compact ? 'mx-2.5 mt-0.5 pb-2 pt-1.5' : 'mx-3.5 mt-0.5 pb-3 pt-2'}`}
        >
          {bodyFooter}
        </div>
      )}

      {actions && (
        <div
          className={`${compact ? 'px-2.5 pb-1.5 pt-0.5' : 'px-3.5 pb-2.5'} ${isUpcoming ? (compact ? 'pt-1' : 'pt-2') : compact ? 'pt-0.5' : 'pt-1'}`}
        >
          {actions}
        </div>
      )}
    </div>
  );
}
